import { useRef, useState, type ReactNode } from "react";

import {
  isSettled,
  uploadFile,
  useFileOperation,
  useFileOperations,
  useStartWorkspaceImport,
} from "../../api/fileOperations";
import { useAuth } from "../../auth/auth-context";
import { Button } from "../../ui/Button";
import { Select } from "../../ui/Select";
import { SettingsPage, SettingsSection } from "../../ui/SettingsSection";
import { FileOperationList } from "./FileOperationList";

/**
 * `/settings/import` (`docs/spec/export-import.md` §5.2).
 *
 * The archive goes up through the ordinary attachment upload — reserve, post, get an id — and
 * only the id reaches `collections.import`. That is why a 100 MB zip is possible at all: the
 * JSON API never carries the bytes.
 */

type PermissionChoice = "read_write" | "read" | "private";

const PERMISSION_OPTIONS = [
  { value: "read_write" as const, label: "Everyone can view and edit" },
  { value: "read" as const, label: "Everyone can view" },
  { value: "private" as const, label: "Private — members are invited individually" },
];

export function ImportScreen(): ReactNode {
  const { session } = useAuth();
  const isAdmin = session?.info.user.role === "admin";
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [permission, setPermission] = useState<PermissionChoice>("read_write");
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [watching, setWatching] = useState<string | null>(null);

  const start = useStartWorkspaceImport();
  const operations = useFileOperations("import");
  const current = useFileOperation(watching);

  const busy = uploading || start.isPending;

  const submit = (): void => {
    if (file === null) {
      return;
    }
    setFailure(null);
    setUploading(true);
    // The upload and the start are one action to the reader, so the button stays busy across
    // both and either failure surfaces in the same place.
    void uploadFile(file, "workspaceImport")
      .then((attachmentId) =>
        start.mutateAsync({
          attachmentId,
          permission: permission === "private" ? null : permission,
        }),
      )
      .then((operation) => {
        setWatching(operation.id);
        setFile(null);
        if (input.current) {
          input.current.value = "";
        }
      })
      .catch((cause: unknown) => {
        setFailure(cause instanceof Error ? cause.message : "The import could not be started");
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const active = current.data;
  const warnings =
    active?.state === "complete" && active.error != null && active.error !== ""
      ? active.error
      : null;

  return (
    <SettingsPage
      title="Import"
      description="Bring a zip of markdown files into the workspace. One directory becomes one collection."
    >
      <SettingsSection
        title="Upload an archive"
        description={
          isAdmin
            ? "A file named `Notes.md` beside a folder named `Notes` becomes one document with children. Files loose at the root go into a collection called Documents."
            : "Only a workspace administrator can import into this workspace."
        }
      >
        <div className="space-y-3">
          <label className="flex flex-col gap-1 text-sm">
            Archive
            <input
              ref={input}
              type="file"
              accept=".zip,application/zip"
              disabled={!isAdmin || busy}
              aria-label="Archive"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setFailure(null);
              }}
              className="rounded-md border border-line bg-raised px-3 py-2 text-sm"
            />
          </label>

          <Select
            label="Who can see the new collections"
            value={permission}
            options={PERMISSION_OPTIONS}
            onChange={setPermission}
            disabled={!isAdmin || busy}
          />

          <Button variant="primary" disabled={!isAdmin || file === null || busy} onClick={submit}>
            {busy ? "Importing…" : "Start import"}
          </Button>

          {failure !== null && (
            <p role="alert" className="text-sm text-danger">
              {failure}
            </p>
          )}

          {active !== undefined && (
            <p role="status" className="text-sm text-muted">
              {active.state === "complete"
                ? `Imported ${active.name}.`
                : active.state === "error"
                  ? `Import failed: ${active.error ?? "unknown error"}`
                  : `Importing ${active.name}…`}
            </p>
          )}
          {warnings !== null && (
            <p className="text-sm text-muted">Some items needed attention: {warnings}</p>
          )}
          {active !== undefined && !isSettled(active) && (
            <p className="text-xs text-muted">This page checks for progress on its own.</p>
          )}
        </div>
      </SettingsSection>

      <SettingsSection title="Recent imports">
        <FileOperationList
          label="Imports"
          operations={operations.data ?? []}
          pending={operations.isPending}
          error={operations.error}
          showDownload={false}
        />
      </SettingsSection>
    </SettingsPage>
  );
}
